import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from "react-redux";
import Axios from "../comp/Utils/axios";
import SummaryAPI from '../comp/Commson/summaryApi';
import toast from 'react-hot-toast';
import { 
    FiSearch, 
    FiFilter, 
    FiRefreshCw, 
    FiUser, 
    FiMail, 
    FiPhone, 
    FiCalendar, 
    FiCheck, 
    FiX, 
    FiAlertCircle, 
    FiEdit, 
    FiSave, 
    FiXCircle,
    FiMoreVertical,
    FiUserCheck,
    FiUserX,
    FiPause,
    FiChevronDown,
    FiChevronUp,
    FiTrash2,
    FiEye,
    FiEyeOff,
    FiKey
} from 'react-icons/fi';

const AdminUserManagement = () => {
  const user = useSelector((state) => state.user);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [actionMenu, setActionMenu] = useState(null);
  const [suspensionReason, setSuspensionReason] = useState('');
  const [showSuspensionModal, setShowSuspensionModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  
  const dropdownRef = useRef(null);
  const actionMenuRefs = useRef({});

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowFilters(false);
      }
      
      // Close action menus when clicking outside
      let clickedOutsideAll = true;
      Object.values(actionMenuRefs.current).forEach(ref => {
        if (ref && ref.contains(event.target)) {
          clickedOutsideAll = false;
        }
      });
      
      if (clickedOutsideAll) {
        setActionMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchUsers();
    }
  }, [user]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await Axios.get(SummaryAPI.getAllUsers.url);
      if (response.data.success) {
        setUsers(response.data.data);
        console.log('Users fetched:', response.data.data); // Debug log
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const updateUserStatus = async (userId, status, reason = '') => {
    try {
      const payload = { status };
      if (status === 'suspended' && reason) {
        payload.reason = reason;
      }

      const response = await Axios.put(
        SummaryAPI.updateUserStatus.url.replace(':userId', userId), 
        payload
      );
      if (response.data.success) {
        setUsers(users.map(user => 
          user._id === userId ? response.data.data : user
        ));
        toast.success(response.data.message);
        setShowSuspensionModal(false);
        setSuspensionReason('');
        setSelectedUser(null);
        setActionMenu(null);
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error(error.response?.data?.message || 'Failed to update user status');
    }
  };

  const deleteUser = async (userId) => {
    try {
        console.log('Deleting user with ID:', userId);
        
        const response = await Axios.delete(
            SummaryAPI.deleteUser.url.replace(':userId', userId)
        );
        
        if (response.data.success) {
            setUsers(users.filter(user => user._id !== userId));
            toast.success(response.data.message);
            setShowDeleteModal(false);
            setSelectedUser(null);
            setActionMenu(null);
        }
    } catch (error) {
        console.error('Error deleting user:', error);
        console.error('Error response:', error.response);
        
        // Show detailed error message
        if (error.response?.data?.message) {
            toast.error(`Delete failed: ${error.response.data.message}`);
        } else {
            toast.error('Failed to delete user. Please check console for details.');
        }
    }
  };

  const resetUserPassword = async (userId) => {
    try {
      const response = await Axios.post(
        SummaryAPI.adminResetPassword.url.replace(':userId', userId)
      );
      if (response.data.success) {
        // Update the user in the list with the new password
        setUsers(users.map(user => 
          user._id === userId 
            ? { ...user, password: response.data.data.temporaryPassword }
            : user
        ));
        toast.success(`Password reset successfully! New password: ${response.data.data.temporaryPassword}`);
        setActionMenu(null);
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(error.response?.data?.message || 'Failed to reset password');
    }
  };

  const handleStatusAction = (user, action) => {
    setSelectedUser(user);
    
    if (action === 'suspended') {
      setShowSuspensionModal(true);
    } else if (action === 'delete') {
      setShowDeleteModal(true);
    } else if (action === 'reset-password') {
      resetUserPassword(user._id);
    } else {
      updateUserStatus(user._id, action);
    }
  };

  const confirmSuspension = () => {
    if (selectedUser) {
      updateUserStatus(selectedUser._id, 'suspended', suspensionReason);
    }
  };

  const confirmDelete = () => {
    if (selectedUser) {
      deleteUser(selectedUser._id);
    }
  };

  const togglePasswordVisibility = (userId) => {
    setShowPasswords(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Function to get the actual password from user data
  const getUserPassword = (user) => {
    // Check multiple possible fields where password might be stored
    return user.password || user.plainPassword || 'No password available';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { 
        color: 'bg-emerald-50 text-emerald-700 border-emerald-200', 
        icon: FiCheck,
        label: 'Active'
      },
      inactive: { 
        color: 'bg-amber-50 text-amber-700 border-amber-200', 
        icon: FiPause,
        label: 'Inactive'
      },
      suspended: { 
        color: 'bg-rose-50 text-rose-700 border-rose-200', 
        icon: FiUserX,
        label: 'Suspended'
      }
    };
    
    const config = statusConfig[status] || statusConfig.active;
    const IconComponent = config.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border ${config.color} transition-colors duration-200`}>
        <IconComponent className="w-3.5 h-3.5 mr-1.5" />
        {config.label}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const isAdmin = role === 'admin';
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium transition-colors duration-200 ${
        isAdmin ? 'bg-violet-50 text-violet-700 border border-violet-200' : 'bg-sky-50 text-sky-700 border border-sky-200'
      }`}>
        <FiUser className="w-3 h-3 mr-1" />
        {isAdmin ? 'Admin' : 'User'}
      </span>
    );
  };

  const getStatusActions = (user) => {
    const currentStatus = user.status;
    
    switch (currentStatus) {
      case 'active':
        return [
          { action: 'inactive', label: 'Make Inactive', icon: FiPause, color: 'text-amber-600 hover:bg-amber-50' },
          { action: 'suspended', label: 'Suspend User', icon: FiUserX, color: 'text-rose-600 hover:bg-rose-50' },
          { action: 'reset-password', label: 'Reset Password', icon: FiKey, color: 'text-blue-600 hover:bg-blue-50' },
          { action: 'delete', label: 'Delete User', icon: FiTrash2, color: 'text-red-600 hover:bg-red-50' }
        ];
      case 'inactive':
        return [
          { action: 'active', label: 'Activate User', icon: FiUserCheck, color: 'text-emerald-600 hover:bg-emerald-50' },
          { action: 'suspended', label: 'Suspend User', icon: FiUserX, color: 'text-rose-600 hover:bg-rose-50' },
          { action: 'reset-password', label: 'Reset Password', icon: FiKey, color: 'text-blue-600 hover:bg-blue-50' },
          { action: 'delete', label: 'Delete User', icon: FiTrash2, color: 'text-red-600 hover:bg-red-50' }
        ];
      case 'suspended':
        return [
          { action: 'active', label: 'Activate User', icon: FiUserCheck, color: 'text-emerald-600 hover:bg-emerald-50' },
          { action: 'inactive', label: 'Make Inactive', icon: FiPause, color: 'text-amber-600 hover:bg-amber-50' },
          { action: 'reset-password', label: 'Reset Password', icon: FiKey, color: 'text-blue-600 hover:bg-blue-50' },
          { action: 'delete', label: 'Delete User', icon: FiTrash2, color: 'text-red-600 hover:bg-red-50' }
        ];
      default:
        return [];
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    
    return matchesSearch && matchesStatus && matchesRole;
  });

  const setActionMenuRef = (userId, el) => {
    actionMenuRefs.current[userId] = el;
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-96 flex items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="text-center p-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <FiAlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600 max-w-sm">
            Administrator privileges are required to access this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                User Management
              </h1>
              <p className="mt-2 text-gray-600">
                Manage user accounts, roles, and access permissions across the platform
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">{users.length}</div>
                <div className="text-sm text-gray-500">Total Users</div>
              </div>
              <button
                onClick={fetchUsers}
                disabled={loading}
                className="inline-flex items-center px-4 py-2.5 bg-white border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50"
              >
                <FiRefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <FiCheck className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
              <div className="ml-4">
                <div className="text-2xl font-bold text-gray-900">
                  {users.filter(u => u.status === 'active').length}
                </div>
                <div className="text-sm text-gray-500">Active Users</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <FiPause className="w-6 h-6 text-amber-600" />
                </div>
              </div>
              <div className="ml-4">
                <div className="text-2xl font-bold text-gray-900">
                  {users.filter(u => u.status === 'inactive').length}
                </div>
                <div className="text-sm text-gray-500">Inactive</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center">
                  <FiUserX className="w-6 h-6 text-rose-600" />
                </div>
              </div>
              <div className="ml-4">
                <div className="text-2xl font-bold text-gray-900">
                  {users.filter(u => u.status === 'suspended').length}
                </div>
                <div className="text-sm text-gray-500">Suspended</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center">
                  <FiUser className="w-6 h-6 text-violet-600" />
                </div>
              </div>
              <div className="ml-4">
                <div className="text-2xl font-bold text-gray-900">
                  {users.filter(u => u.role === 'admin').length}
                </div>
                <div className="text-sm text-gray-500">Admins</div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl bg-gray-50 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
              </div>
            </div>

            {/* Filter Toggle for Mobile */}
            <div className="lg:hidden">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-100 transition-colors duration-200"
              >
                <span>Filters</span>
                {showFilters ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
              </button>
            </div>

            {/* Filters */}
            <div ref={dropdownRef} className={`${showFilters ? 'block' : 'hidden'} lg:flex lg:flex-row lg:space-x-4 space-y-4 lg:space-y-0`}>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full lg:w-48 px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>

              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full lg:w-48 px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="all">All Roles</option>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mb-4"></div>
              <p className="text-gray-600">Loading users...</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                        Contact
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Password
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                        Role
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden xl:table-cell">
                        Joined
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredUsers.map((user, index) => (
                      <tr key={user._id} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              {user.avatar ? (
                                <img className="h-10 w-10 rounded-full object-cover border border-gray-200" src={user.avatar} alt={user.name} />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center border border-gray-200">
                                  <FiUser className="h-5 w-5 text-white" />
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.name}
                              </div>
                              <div className="text-sm text-gray-500 md:hidden">
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 hidden md:table-cell">
                          <div className="text-sm text-gray-900 flex items-center">
                            <FiMail className="w-4 h-4 mr-2 text-gray-400" />
                            {user.email}
                          </div>
                          {user.mobile && (
                            <div className="text-sm text-gray-500 flex items-center mt-1">
                              <FiPhone className="w-4 h-4 mr-2 text-gray-400" />
                              {user.mobile}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <FiKey className="w-4 h-4 mr-2 text-gray-400" />
                            <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                              {showPasswords[user._id] ? getUserPassword(user) : '••••••••'}
                            </span>
                            <button
                              onClick={() => togglePasswordVisibility(user._id)}
                              className="ml-2 p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                            >
                              {showPasswords[user._id] ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(user.status)}
                        </td>
                        <td className="px-6 py-4 hidden lg:table-cell">
                          {getRoleBadge(user.role)}
                        </td>
                        <td className="px-6 py-4 hidden xl:table-cell">
                          <div className="flex items-center text-sm text-gray-500">
                            <FiCalendar className="w-4 h-4 mr-2 text-gray-400" />
                            {new Date(user.createdAt).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 relative">
                          <div className="relative" ref={el => setActionMenuRef(user._id, el)}>
                            <button
                              onClick={() => setActionMenu(actionMenu === user._id ? null : user._id)}
                              className="inline-flex items-center p-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                            >
                              <FiMoreVertical className="w-4 h-4" />
                            </button>

                            {actionMenu === user._id && (
                              <div className={`absolute right-0 mt-1 w-56 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 border border-gray-200 ${
                                // Check if it's the last few rows and position dropdown above
                                index >= filteredUsers.length - 3 ? 'bottom-full mb-1' : 'top-full'
                              }`}>
                                <div className="py-1">
                                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                                    User Actions
                                  </div>
                                  {getStatusActions(user).map((action) => (
                                    <button
                                      key={action.action}
                                      onClick={() => handleStatusAction(user, action.action)}
                                      className={`flex items-center w-full px-4 py-2.5 text-sm ${action.color} transition-colors duration-150`}
                                    >
                                      <action.icon className="w-4 h-4 mr-3" />
                                      {action.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {!loading && filteredUsers.length === 0 && (
                <div className="text-center py-16">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                    <FiUser className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
                  <p className="text-gray-500 max-w-sm mx-auto">
                    {searchTerm || statusFilter !== 'all' || roleFilter !== 'all' 
                      ? 'Try adjusting your search or filter criteria.' 
                      : 'No users available in the system.'}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Suspension Reason Modal */}
      {showSuspensionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full transform transition-all">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 bg-rose-100 rounded-full mx-auto mb-4">
                <FiUserX className="w-6 h-6 text-rose-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Suspend User
              </h3>
              <p className="text-gray-600 text-center mb-6">
                Please provide a reason for suspending <span className="font-semibold">{selectedUser?.name}</span>'s account
              </p>
              
              <textarea
                value={suspensionReason}
                onChange={(e) => setSuspensionReason(e.target.value)}
                placeholder="Enter suspension reason..."
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none"
                rows="4"
              />
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowSuspensionModal(false);
                    setSuspensionReason('');
                    setSelectedUser(null);
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSuspension}
                  disabled={!suspensionReason.trim()}
                  className="flex-1 px-4 py-3 bg-rose-600 text-white rounded-xl hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  Suspend User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full transform transition-all">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
                <FiTrash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Delete User
              </h3>
              <p className="text-gray-600 text-center mb-6">
                Are you sure you want to permanently delete <span className="font-semibold">{selectedUser?.name}</span>'s account? This action cannot be undone.
              </p>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedUser(null);
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all duration-200"
                >
                  Delete User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserManagement;